"use client";
import { Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { type SyntheticEvent, useId, useState } from "react";
import clsx from "clsx";

import styles from "./NmiSearchModal.module.scss";

import { Picture } from "@/components/shared/Picture/Picture";
import type { ImageQueryResult, NmiSearchModalContent } from "@/sanity/queries";
import { ButtonLink } from "../Button/Button";
import { MaterialIcon } from "../MaterialIcon/MaterialIcon";

interface NmiSearchModalProps {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	image?: ImageQueryResult | null;
	content?: NmiSearchModalContent | null;
	onNmiSearch?: (nmi: string) => void | Promise<void>;
}

const defaultContent = {
	title: "Find your connection using your connection National Meter Identifier (NMI)",
	description:
		"Your NMI number is a 10 digit number found near your Supply Address on your most recent statement.",
	footerTitle: "I don’t know my NMI number",
	footerText: "Contact us, we’ll call you back to get connected.",
};

const searchInputPlaceholder = "NMI Number";
const searchInputAriaLabel = "NMI Number";
const searchButtonLabel = "Search";

export const NmiSearchModal = ({
	isOpen,
	onOpenChange,
	image: imageOverride,
	content,
	onNmiSearch,
}: NmiSearchModalProps) => {
	const image = imageOverride ?? content?.image;
	const contactCta = content?.contactCta;
	const errorId = useId();
	const [nmi, setNmi] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const validateNmi = (value: string) => {
		const trimmedValue = value.trim();

		if (!trimmedValue) {
			return {
				error: "Please enter your NMI number.",
				normalizedNmi: "",
			};
		}

		if (/[^\d\s]/.test(trimmedValue)) {
			return {
				error: "Please enter digits only.",
				normalizedNmi: "",
			};
		}

		const normalizedNmi = trimmedValue.replace(/\s/g, "");

		if (!/^\d{10,11}$/.test(normalizedNmi)) {
			return {
				error: "Please enter a 10 or 11 digit NMI number.",
				normalizedNmi: "",
			};
		}

		return {
			error: "",
			normalizedNmi,
		};
	};

	const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
		event.preventDefault();

		const validation = validateNmi(nmi);
		setError(validation.error);

		if (validation.error || !onNmiSearch) {
			return;
		}

		setIsSubmitting(true);

		try {
			await onNmiSearch(validation.normalizedNmi);
			onOpenChange(false);
			setIsSubmitting(false);
		} catch (error) {
			console.error("Failed to search NMI", error);
			setError("We couldn't search that NMI. Please try again.");
			setIsSubmitting(false);
		}
	};

	return (
		<ModalOverlay
			className={styles["overlay"]}
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			isDismissable
		>
			<Modal className={styles["modal"]}>
				<Dialog className={styles["dialog"]}>
					{({ close }) => (
						<>
							<div className={styles["container"]}>
								<div className={styles["copy-row"]}>
									<div className={styles["copy"]}>
										<Heading
											level={2}
											slot="title"
											className={styles["title"]}
										>
											{content?.title ?? defaultContent.title}
										</Heading>
										<p className={styles["description"]}>
											{content?.description ?? defaultContent.description}
										</p>
									</div>

									<div className={styles["image"]}>
										{image?.image && (
											<Picture
												className={styles["image-picture"]}
												sanityImage={image.image}
												alt={image.altText}
												width={282}
												height={162}
												crop
											/>
										)}
									</div>
								</div>

								<form
									className={styles["search"]}
									onSubmit={handleSubmit}
									noValidate
								>
									<div
										className={styles["search-icon"]}
										aria-hidden="true"
									>
										<MaterialIcon name="search" />
									</div>
									<input
										className={clsx(
											styles["search-input"],
											error && styles["search-input--error"],
										)}
										type="text"
										placeholder={searchInputPlaceholder}
										aria-label={searchInputAriaLabel}
										aria-invalid={Boolean(error)}
										aria-describedby={error ? errorId : undefined}
										autoComplete="off"
										inputMode="numeric"
										value={nmi}
										onChange={event => {
											setNmi(event.target.value);
											if (error) {
												setError("");
											}
										}}
									/>
									<button
										type="submit"
										className={styles["search-button"]}
										disabled={isSubmitting}
									>
										{isSubmitting ? "Searching" : searchButtonLabel}
										<span
											className={styles["search-button__icon"]}
											aria-hidden="true"
										>
											<MaterialIcon name="arrow_right_alt" />
										</span>
									</button>
									{error && (
										<p
											className={styles["search-error"]}
											id={errorId}
										>
											{error}
										</p>
									)}
								</form>
							</div>

							<div className={styles["footer"]}>
								<div className={styles["footer-copy"]}>
									<p className={styles["footer-title"]}>
										{content?.footerTitle ?? defaultContent.footerTitle}
									</p>
									<p className={styles["footer-text"]}>
										{content?.footerText ?? defaultContent.footerText}
									</p>
								</div>
								{contactCta?.link && contactCta.label && (
									<ButtonLink
										href={contactCta.link}
										theme="tertiary"
										label={contactCta.label}
										target={contactCta.openInNewTab ? "_blank" : undefined}
									/>
								)}
							</div>

							<button
								type="button"
								className={styles["close"]}
								onClick={close}
								aria-label="Close"
							>
								<MaterialIcon name="close" />
							</button>
						</>
					)}
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
};
